import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  InboxAddress, InboxEmail, EmailBodyMode, ClientInbox,
  getInbox, createInboxAddress, saveInboxSettings, listClientInboxes,
} from '../../../lib/emailStore';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import './EmailInboxTab.css';

// =============================================
// EmailInboxTab — the "Forward Emails to File" tab of Upload Docs
// =============================================
// Shows a private forwarding address — the login's own, or (clientId, under Client's
// Accounts) the address of the client being managed — the rules for mail sent to it, and
// what has arrived. Addresses are made automatically (server: at signup / when a client
// is added, or on first view), so there is nothing to create. The own view also lists
// every client's address, to hand them out. The mail is filed by the SERVER the moment it
// lands (server/lib/emailInbound.js) — nothing here uploads anything.
// =============================================

interface OwnerOption {
  id: number;
  name: string;
  type: 'personal' | 'family' | 'business' | 'client' | 'client_business';
}

interface EmailInboxTabProps {
  /** The Upload page's owner list (own account: owner/family/businesses; a client: client/family/businesses). */
  owners: OwnerOption[];
  /** Open a filed document in Process Docs / Receipts. */
  onOpenDocument: (documentId: number) => void;
  /** Client's Accounts: the client whose address this is (null/absent = the login's own). */
  clientId?: number | null;
  clientName?: string;
}

interface SettingsForm {
  ownerKey: string;             // '' = choose later | '<owner_type>:<owner_id>'
  bodyMode: EmailBodyMode;
  anySender: boolean;
  allowedSenders: string;
}

// The Upload page offsets business ids by 10000 so one list can hold people and businesses.
function ownerKeyOf(o: OwnerOption): string | null {
  if (o.type === 'personal') return `account_owner:${o.id}`;
  if (o.type === 'family') return `family_member:${o.id}`;
  if (o.type === 'business') return `business:${o.id - 10000}`;
  if (o.type === 'client') return `client_personal:${o.id}`;
  if (o.type === 'client_business') return `client_business:${o.id - 10000}`;
  return null;
}

function formOf(a: InboxAddress): SettingsForm {
  return {
    ownerKey: a.owner_type && a.owner_id != null ? `${a.owner_type}:${a.owner_id}` : '',
    bodyMode: a.body_mode,
    anySender: a.allow_any_sender,
    allowedSenders: a.allowed_senders || '',
  };
}

const STATUS_LABEL: Record<InboxEmail['status'], string> = {
  filed: 'Filed', empty: 'Nothing to file', rejected: 'Refused', duplicate: 'Already filed', error: 'Could not be read',
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const EmailInboxTab: React.FC<EmailInboxTabProps> = ({ owners, onOpenDocument, clientId = null, clientName = '' }) => {
  const isClient = clientId != null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [address, setAddress] = useState<InboxAddress | null>(null);
  const [recent, setRecent] = useState<InboxEmail[]>([]);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const formRef = useRef<SettingsForm | null>(null);
  formRef.current = form;

  const load = useCallback(async (keepForm = false) => {
    try {
      const state = await getInbox(clientId);
      setEnabled(state.enabled);
      setAddress(state.address);
      setRecent(state.recent || []);
      if (!keepForm) setForm(state.address ? formOf(state.address) : null);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load your address.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const change = useCallback((patch: Partial<SettingsForm>) => {
    setForm(prev => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
    setFormDirty(true);
    setNotice(null);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const f = formRef.current;
    if (!f) return true;
    setBusy(true);
    try {
      const [ownerType, ownerId] = f.ownerKey ? f.ownerKey.split(':') : [null, null];
      const state = await saveInboxSettings({
        owner_type: ownerType,
        owner_id: ownerId != null ? Number(ownerId) : null,
        body_mode: f.bodyMode,
        allow_any_sender: f.anySender,
        allowed_senders: f.allowedSenders,
      }, clientId);
      setAddress(state.address);
      if (state.address) setForm(formOf(state.address));
      setDirty(false);
      setFormDirty(false);
      setError(null);
      setNotice('Saved.');
      return true;
    } catch (e: any) {
      setError(e?.message || 'Could not save.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [clientId]);

  // "Save & continue" in the unsaved-changes prompt saves these settings.
  useEffect(() => {
    if (!dirty) return undefined;
    registerSaveHandler(save);
    return () => registerSaveHandler(null);
  }, [dirty, save]);
  // Leaving the tab with nothing kept must not leave the app thinking a form is dirty.
  useEffect(() => () => { setFormDirty(false); }, []);

  const create = useCallback(async (regenerate: boolean) => {
    setBusy(true);
    setConfirmReplace(false);
    try {
      const state = await createInboxAddress(regenerate, clientId);
      setEnabled(state.enabled);
      setAddress(state.address);
      if (!regenerate && state.address) setForm(formOf(state.address));
      setError(null);
      setNotice(regenerate ? 'Your address was replaced. The old one no longer works.' : null);
    } catch (e: any) {
      setError(e?.message || 'Could not create the address.');
    } finally {
      setBusy(false);
    }
  }, [clientId]);

  // On / off takes effect at once — it is a switch, not part of the settings form.
  const toggleActive = useCallback(async (next: boolean) => {
    setBusy(true);
    try {
      const state = await saveInboxSettings({ is_active: next }, clientId);
      setAddress(state.address);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not change the address.');
    } finally {
      setBusy(false);
    }
  }, [clientId]);

  const copy = useCallback(async () => {
    if (!address?.email) return;
    try {
      await navigator.clipboard.writeText(address.email);
      setNotice('Address copied.');
    } catch {
      setNotice('Select the address and copy it.');
    }
  }, [address]);

  if (loading) return <p className="ei-muted">Loading your address…</p>;

  if (!address) {
    return (
      <div className="ei-wrap">
        {error && <div className="ei-error" role="alert">{error}</div>}
        <div className="ei-card">
          <p className="ei-lead">The forwarding address could not be loaded.</p>
          <p className="ei-muted">Addresses are made automatically — try again in a moment.</p>
          <button type="button" className="ud-btn ud-btn-primary" disabled={busy} onClick={() => create(false)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const ownerGroups: { label: string; options: OwnerOption[] }[] = (isClient ? [
    { label: 'Client & Family', options: owners.filter(o => o.type === 'client') },
    { label: "Client's Businesses", options: owners.filter(o => o.type === 'client_business') },
  ] : [
    { label: 'Account Owners', options: owners.filter(o => o.type === 'personal') },
    { label: 'Family Members', options: owners.filter(o => o.type === 'family') },
    { label: 'Businesses', options: owners.filter(o => o.type === 'business') },
  ]).filter(g => g.options.length > 0);
  const who = clientName || 'this client';

  return (
    <div className="ei-wrap">
      {error && <div className="ei-error" role="alert">{error}</div>}

      <div className="ei-card">
        <label className="ud-client-label" htmlFor="ei-address">
          {isClient ? `${who}'s forwarding address` : 'Your forwarding address'}
        </label>
        {isClient && (
          <p className="ei-muted">
            Give this address to {who}: anything they (or you) forward to it lands in {who}'s documents here.
          </p>
        )}
        {address.email ? (
          <div className="ei-address-row">
            <input id="ei-address" className="ei-address" readOnly value={address.email}
              onFocus={e => e.currentTarget.select()} />
            <button type="button" className="ud-btn ud-btn-primary" onClick={copy}>Copy</button>
          </div>
        ) : (
          <p className="ei-muted">Your address is reserved and will be shown here once receiving is switched on.</p>
        )}
        {!enabled && (
          <div className="ei-warning">
            Receiving email is not switched on for this server yet, so mail sent to this address will not arrive.
          </div>
        )}
        {enabled && !address.is_active && (
          <div className="ei-warning">This address is switched off — mail sent to it is refused.</div>
        )}
        <div className="ei-address-actions">
          <label className="ei-check">
            <input type="checkbox" checked={address.is_active} disabled={busy}
              onChange={e => toggleActive(e.target.checked)} />
            Accept mail at this address
          </label>
          {confirmReplace ? (
            <span className="ei-confirm">
              The current address will stop working.
              <button type="button" className="ud-btn ud-btn-secondary" disabled={busy} onClick={() => create(true)}>Replace it</button>
              <button type="button" className="ud-btn ud-btn-secondary" onClick={() => setConfirmReplace(false)}>Keep it</button>
            </span>
          ) : (
            <button type="button" className="ud-btn ud-btn-secondary" disabled={busy} onClick={() => setConfirmReplace(true)}>
              Replace address…
            </button>
          )}
        </div>
        {notice && <p className="ei-notice" role="status">{notice}</p>}
      </div>

      {form && (
        <form className="ei-card" onSubmit={e => { e.preventDefault(); void save(); }}>
          <h3 className="ei-heading">How forwarded email is filed</h3>

          <div className="ei-field">
            <label className="ud-client-label" htmlFor="ei-owner">Owner of the documents</label>
            <select id="ei-owner" className="ud-client-select" value={form.ownerKey}
              onChange={e => change({ ownerKey: e.target.value })}>
              {!isClient && <option value="">Choose when filing</option>}
              {ownerGroups.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map(o => {
                    const key = ownerKeyOf(o);
                    return key ? <option key={key} value={key}>{o.name}</option> : null;
                  })}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="ei-field">
            <label className="ud-client-label" htmlFor="ei-body">Put the email message in front of its attachments</label>
            <select id="ei-body" className="ud-client-select" value={form.bodyMode}
              onChange={e => change({ bodyMode: e.target.value as EmailBodyMode })}>
              <option value="always">Always — the message is the first page</option>
              <option value="auto">Only when the email has no PDF or image attached</option>
              <option value="never">Never — attachments only</option>
            </select>
          </div>

          <fieldset className="ei-field ei-fieldset">
            <legend className="ud-client-label">Who may send to this address</legend>
            <label className="ei-check">
              <input type="radio" name="ei-sender" checked={!form.anySender} onChange={() => change({ anySender: false })} />
              {isClient
                ? `Only my account's sign-in emails, ${who}'s and their family's emails, plus the senders listed below`
                : 'Only the sign-in email addresses of my account, plus the senders listed below'}
            </label>
            <label className="ei-check">
              <input type="radio" name="ei-sender" checked={form.anySender} onChange={() => change({ anySender: true })} />
              Anyone who has the address (for example a supplier who emails invoices straight to it)
            </label>
          </fieldset>

          {!form.anySender && (
            <div className="ei-field">
              <label className="ud-client-label" htmlFor="ei-senders">Other senders to accept</label>
              <textarea id="ei-senders" className="ud-notes-input" rows={3}
                placeholder={'another.address@example.com\n@mycompany.com'}
                value={form.allowedSenders}
                onChange={e => change({ allowedSenders: e.target.value })} />
              <p className="ud-engine-hint">One per line: an email address, or @domain.com for everyone at that domain.</p>
            </div>
          )}

          <button type="submit" className="ud-btn ud-btn-primary" disabled={busy || !dirty}>Save</button>
        </form>
      )}

      {!isClient && <ClientAddressList />}

      <div className="ud-history">
        <div className="ei-history-head">
          <h3 className="ud-history-title">Emails received ({recent.length})</h3>
          <button type="button" className="ud-btn ud-btn-secondary" onClick={() => load(true)}>Refresh</button>
        </div>
        {recent.length === 0 ? (
          <p className="ei-muted">Nothing has arrived yet.</p>
        ) : (
          <div className="ud-history-table-wrap">
            <table className="ud-history-table">
              <thead>
                <tr><th>Received</th><th>From</th><th>Subject</th><th>Result</th></tr>
              </thead>
              <tbody>
                {recent.map(m => (
                  <tr key={m.inbound_email_id}>
                    <td className="ei-nowrap">{formatWhen(m.received_at)}</td>
                    <td>{m.from_name || m.from_email || '—'}{m.from_name && m.from_email ? <span className="ei-sub"> {m.from_email}</span> : null}</td>
                    <td>{m.subject || '(no subject)'}</td>
                    <td>
                      <span className={`ud-badge ${m.status === 'filed' ? 'ud-badge-done' : m.status === 'duplicate' ? 'ud-badge-queued' : 'ud-badge-error'}`}>
                        {STATUS_LABEL[m.status]}
                      </span>
                      {m.status === 'filed' && (m.document_ids || []).map((id, i) => (
                        <button key={id} type="button" className="ei-doc-link" onClick={() => onOpenDocument(id)}
                          title="Open in Process Docs / Receipts">
                          Document {i + 1}
                        </button>
                      ))}
                      {m.reason && <div className="ei-sub">{m.reason}</div>}
                      {(m.skipped || []).length > 0 && (
                        <div className="ei-sub">Not filed: {m.skipped.map(s => s.filename).join(', ')}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Every client's address in one place, to hand out. Rules for one client's address are
// changed on that client's own tab (Client's Accounts → Upload Documents).
const ClientAddressList: React.FC = () => {
  const [clients, setClients] = useState<ClientInbox[] | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    listClientInboxes()
      .then(r => { if (!cancelled) setClients(r.clients); })
      .catch(() => { if (!cancelled) setClients([]); });
    return () => { cancelled = true; };
  }, []);
  if (!clients || clients.length === 0) return null;
  const copy = async (c: ClientInbox) => {
    if (!c.address?.email) return;
    try { await navigator.clipboard.writeText(c.address.email); setCopied(c.client_id); } catch { /* select by hand */ }
  };
  return (
    <div className="ei-card">
      <h3 className="ei-heading">Your clients' addresses ({clients.length})</h3>
      <p className="ei-muted">
        Each client has their own address; mail to it is filed in that client's documents under Client's Accounts.
        Change a client's rules from Client's Accounts → Upload Documents → Forward Emails to File.
      </p>
      <div className="ud-history-table-wrap">
        <table className="ud-history-table">
          <thead><tr><th>Client</th><th>Address</th><th></th></tr></thead>
          <tbody>
            {clients.map(c => (
              <tr key={c.client_id}>
                <td>{c.name}</td>
                <td className="ei-sub">
                  {c.address?.email || 'shown once receiving is switched on'}
                  {c.address && !c.address.is_active ? ' (switched off)' : ''}
                </td>
                <td>
                  {c.address?.email && (
                    <button type="button" className="ud-btn ud-btn-secondary" onClick={() => copy(c)}>
                      {copied === c.client_id ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmailInboxTab;
